/**
 * Approval decisions, undo, manual handoff, the T4 hold proof and revert
 * (specs/c2-kontratlar.md 2.4.2, 2.5, 2.6). Request bodies are strict: an unknown
 * field is a 400 invalid_request.
 */
import { z } from 'zod';
import { approvalStatusSchema, boundedText, executionStatusSchema, idStringSchema, isoDateTimeSchema, rejectReasonCodeSchema, sha256Schema, telegramUserIdSchema, } from '../ids.js';
/** Longest decision note in code points (the bot limits its "Düzelt" note to 1000 of them). */
export const DECISION_NOTE_MAX = 1000;
/** Longest edited text of "Düzelt ve onayla" in the panel (customer reply limit). */
export const EDITED_TEXT_MAX = 4000;
/** A hold token is short (two base64url parts); anything longer is not one. */
export const HOLD_TOKEN_MAX = 2048;
const note = boundedText(1, DECISION_NOTE_MAX);
const holdToken = z.string().min(1).max(HOLD_TOKEN_MAX);
const approvePanel = z.strictObject({
    decision: z.literal('approve'),
    payloadHash: sha256Schema,
    channel: z.literal('panel'),
    /** Required for every tier other than T3 (2.5 step 9). */
    holdToken: holdToken.optional(),
    /** Required (true) from the panel for manual_copy actions. */
    copyConfirmed: z.boolean().optional(),
    note: note.optional(),
});
/** A confirm token is exactly two base64url parts (2.6a); anything else is not one. */
export const CONFIRM_TOKEN_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const approveTelegram = z
    .strictObject({
    decision: z.literal('approve'),
    payloadHash: sha256Schema,
    channel: z.literal('telegram'),
    telegramUserId: telegramUserIdSchema,
    /** Required for every tier other than T3: the second tap of the two-step confirm (2.6a). */
    confirmToken: z.string().min(1).max(HOLD_TOKEN_MAX).regex(CONFIRM_TOKEN_PATTERN).optional(),
    /** Required with confirmToken: the private chat the confirm was issued for. */
    telegramChatId: z.number().int().optional(),
    note: note.optional(),
})
    .refine((body) => (body.confirmToken === undefined) === (body.telegramChatId === undefined), {
    error: 'confirmToken and telegramChatId come together',
    path: ['telegramChatId'],
});
const editApprovePanel = z.strictObject({
    decision: z.literal('edit_approve'),
    payloadHash: sha256Schema,
    channel: z.literal('panel'),
    editedText: z.string().min(1).max(EDITED_TEXT_MAX),
    note: note.optional(),
});
/** Telegram "Düzelt": becomes a rejection with a revision request (2.11 item 2). */
const editApproveTelegram = z.strictObject({
    decision: z.literal('edit_approve'),
    payloadHash: sha256Schema,
    channel: z.literal('telegram'),
    telegramUserId: telegramUserIdSchema,
    note,
});
const rejectPanel = z.strictObject({
    decision: z.literal('reject'),
    payloadHash: sha256Schema,
    channel: z.literal('panel'),
    rejectReasonCode: rejectReasonCodeSchema,
    note: note.optional(),
});
const rejectTelegram = z.strictObject({
    decision: z.literal('reject'),
    payloadHash: sha256Schema,
    channel: z.literal('telegram'),
    telegramUserId: telegramUserIdSchema,
    rejectReasonCode: rejectReasonCodeSchema,
    note: note.optional(),
});
/** POST /api/v1/approvals/{id}/decision: split by decision, then by channel. */
export const decisionRequestSchema = z.discriminatedUnion('decision', [
    z.discriminatedUnion('channel', [approvePanel, approveTelegram]),
    z.discriminatedUnion('channel', [editApprovePanel, editApproveTelegram]),
    z.discriminatedUnion('channel', [rejectPanel, rejectTelegram]),
]);
/** Prefix of decision_note_tr when a Telegram "Düzelt" note is stored as a rejection. */
export const REVISION_NOTE_PREFIX = 'Düzeltme isteği: ';
export const decisionResponseSchema = z.object({
    approvalId: idStringSchema,
    status: approvalStatusSchema,
    decision: z.enum(['approve', 'edit_approve', 'reject']).nullable(),
    decidedAt: isoDateTimeSchema.nullable(),
    /** decided_at + action_types.undo_seconds. */
    undoUntil: isoDateTimeSchema.nullable(),
    executionStatus: executionStatusSchema,
    /** The approved hash (the edited content's after "Düzelt ve onayla"). */
    payloadHash: sha256Schema,
    /** Only for a Telegram "Düzelt" note: the linked task that runs again (2.11). */
    revisionTaskId: idStringSchema.nullable(),
});
/** POST /api/v1/approvals/{id}/undo and /manual-done. */
export const undoRequestSchema = z.discriminatedUnion('channel', [
    z.strictObject({ channel: z.literal('panel') }),
    z.strictObject({ channel: z.literal('telegram'), telegramUserId: telegramUserIdSchema }),
]);
export const undoResponseSchema = z.object({
    approvalId: idStringSchema,
    status: z.literal('undone'),
    /** The new pending proposal that went back to the queue; null when a newer one for the same subject was there. */
    requeuedApprovalId: idStringSchema.nullable(),
});
export const manualDoneRequestSchema = undoRequestSchema;
export const manualDoneResponseSchema = z.object({
    approvalId: idStringSchema,
    executionStatus: z.literal('manual_done'),
});
/** POST /api/v1/approvals/{id}/send (v0.1.54): Gönder on an approved e-mail reply; the worker sends it through Gmail. */
export const sendReplyRequestSchema = undoRequestSchema;
export const sendReplyResponseSchema = z.object({
    approvalId: idStringSchema,
    status: z.enum(['queued', 'sending', 'sent', 'failed']),
});
/** POST /api/v1/approvals/{id}/hold. */
export const holdRequestSchema = z.strictObject({ payloadHash: sha256Schema });
export const holdResponseSchema = z.object({
    holdToken: z.string().min(1).max(HOLD_TOKEN_MAX),
    serverNow: isoDateTimeSchema,
    notBefore: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema,
});
/**
 * POST /api/v1/approvals/{id}/telegram-confirm (bot): the first tap on "Onayla" of a
 * non-T3 card. Nothing is approved; the API records a Telegram approval view and returns
 * the proposal read fresh plus a confirm token for the second tap (2.6a).
 */
export const telegramConfirmRequestSchema = z.strictObject({
    payloadHash: sha256Schema,
    telegramUserId: telegramUserIdSchema,
    telegramChatId: z.number().int(),
    /** The message that shows the re-rendered confirm card (approval_views.telegram_message_id). */
    telegramMessageId: z.number().int().positive(),
    /** Telegram's chat.type; only 'private' is accepted (403 private_chat_required). */
    chatType: z.enum(['private', 'group', 'supergroup', 'channel']),
});
export const telegramConfirmResponseSchema = z.object({
    confirmToken: z.string().min(1).max(HOLD_TOKEN_MAX).regex(CONFIRM_TOKEN_PATTERN),
    serverNow: isoDateTimeSchema,
    notBefore: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema,
    /** view_id of the Telegram approval_views row the DB guard links the approval to. */
    viewId: idStringSchema,
});
/** POST /api/v1/done/{approvalId}/revert: no body; the Idempotency-Key header is required. */
export const revertResponseSchema = decisionResponseSchema;
/** POST /api/v1/approvals/{id}/views (bot, optional in C2): the Telegram card was shown. */
export const approvalViewRequestSchema = z.strictObject({
    payloadHash: sha256Schema,
    telegramUserId: telegramUserIdSchema,
    telegramChatId: z.number().int(),
    telegramMessageId: z.number().int().positive(),
});
export const approvalViewResponseSchema = z.object({ viewId: idStringSchema });
// ---------------------------------------------------------------------------
// T4 hold proof (2.6)
// ---------------------------------------------------------------------------
/** The panel's hold lasts 1.5 s; the server accepts from 1.4 s (100 ms for the network). */
export const PANEL_HOLD_MS = 1500;
export const HOLD_MIN_MS = 1400;
/** A hold token lives 60 s. */
export const HOLD_TOKEN_TTL_MS = 60_000;
/** TWIN_HOLD_TOKEN_KEY must be at least 32 bytes. */
export const HOLD_TOKEN_KEY_MIN_BYTES = 32;
/**
 * Claims of holdToken = base64url(JSON(claims)) + "." + base64url(HMAC_SHA256(key, first part)).
 * Stateless: a restart does not invalidate a token; jti is kept in memory until exp.
 */
export const holdTokenClaimsSchema = z.strictObject({
    v: z.literal(1),
    /** "<approval_id>" | "switch:<key>" | "agent:<agentId>". */
    aid: z.string().min(1).max(200),
    /** payload_hash of the approval, or sha256 of holdPayloadFor... below. */
    ph: sha256Schema,
    hid: z.string().min(1).max(64),
    /** view_id of the panel approval_views row; only for approvals. */
    vid: idStringSchema.nullable(),
    iat: z.number().int().nonnegative(),
    nbf: z.number().int().nonnegative(),
    exp: z.number().int().nonnegative(),
    /** 128 random bits, base64url. */
    jti: z.string().regex(/^[A-Za-z0-9_-]{22,64}$/),
});
// ---------------------------------------------------------------------------
// Telegram two-step confirm (2.6a, decision 1 of 2026-09-27)
// ---------------------------------------------------------------------------
/** The second tap is accepted from 3 s after the confirm card was issued. */
export const TELEGRAM_CONFIRM_MIN_MS = 3000;
/** A confirm token lives 60 s. */
export const TELEGRAM_CONFIRM_TTL_MS = 60_000;
/**
 * Domain prefix of the signed part: HMAC input is TELEGRAM_CONFIRM_DOMAIN + first part, so a
 * confirm token never verifies as a panel hold token (and the reverse), even with one key.
 */
export const TELEGRAM_CONFIRM_DOMAIN = 'twin-telegram-confirm.v1.';
/**
 * Claims of confirmToken = base64url(JSON(claims)) + "." +
 * base64url(HMAC_SHA256(TWIN_HOLD_TOKEN_KEY, TELEGRAM_CONFIRM_DOMAIN + first part)).
 * The API accepts it only if now >= nbf (else 409 confirm_too_fast), now < exp (else 409
 * confirm_expired), jti unused (else 409 confirm_replayed), ph still the current
 * payload_hash (else 409 stale_view), tid the configured owner and cid a private chat.
 */
export const telegramConfirmClaimsSchema = z.strictObject({
    v: z.literal(1),
    ch: z.literal('telegram'),
    /** approval_id; only approvals, never switches or agents (those stay panel-only). */
    aid: idStringSchema,
    ph: sha256Schema,
    hid: z.string().min(1).max(64),
    /** view_id of the Telegram approval_views row. */
    vid: idStringSchema,
    /** Telegram user id and private chat id the token was issued to. */
    tid: z.number().int().positive(),
    cid: z.number().int(),
    iat: z.number().int().nonnegative(),
    nbf: z.number().int().nonnegative(),
    exp: z.number().int().nonnegative(),
    jti: z.string().regex(/^[A-Za-z0-9_-]{22,64}$/),
});
/** aid of a switch hold. */
export function switchHoldAid(key) {
    return `switch:${key}`;
}
/** aid of a growth agent resume hold. */
export function agentHoldAid(agentId) {
    return `agent:${agentId}`;
}
/** The text whose sha256 is ph for a switch hold: "switch:<key>:<enabled>". */
export function switchHoldSubject(key, enabled) {
    return `switch:${key}:${enabled}`;
}
/** The text whose sha256 is ph for a growth agent resume hold: "agent:<agentId>:active". */
export function agentHoldSubject(agentId) {
    return `agent:${agentId}:active`;
}
//# sourceMappingURL=decisions.js.map